import React, { useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const colorPalette = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#00c49f",
  "#ff0000",
];

const transformData = (rawData) => {
  if (!rawData || Object.keys(rawData).length === 0) return [];

  const keys = Object.keys(rawData);
  const maxLength = Math.max(...keys.map((key) => rawData[key].length));

  return Array.from({ length: maxLength }).map((_, i) => {
    const firstNonEmptyKey = keys.find((key) => rawData[key][i]);
    const timestamp = rawData[firstNonEmptyKey]?.[i]?.x;

    const time = timestamp
      ? new Date(timestamp).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : `--:--`;

    const entry = { time };

    keys.forEach((key) => {
      entry[key] = rawData[key]?.[i]?.y ?? null;
    });

    return entry;
  });
};

const DynamicLineGraph = ({ seriesData,translate }) => {
  const [data, setData] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [zoomFactor, setZoomFactor] = useState(1);
  const [hiddenLines, setHiddenLines] = useState({});
  const chartRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartIndex = useRef(0);

  useEffect(() => {
    if (seriesData) {
      const transformed = transformData(seriesData);
      setData(transformed);

      const initialHidden = {};
      Object.keys(seriesData).forEach((key) => {
        initialHidden[key] = false;
      });
      setHiddenLines(initialHidden);
    }
  }, [seriesData]);

  const handleWheel = (e) => {
    e.preventDefault();
    const rect = chartRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const percent = mouseX / rect.width;

    setZoomFactor((prevZoom) => {
      const newZoom = e.deltaY < 0 ? Math.min(prevZoom * 1.1, 10) : Math.max(prevZoom / 1.1, 1);
      const visibleCount = Math.floor(data.length / newZoom);
      const newStart = Math.max(
        0,
        Math.min(data.length - visibleCount, startIndex + Math.floor((percent - 0.5) * visibleCount))
      );
      setStartIndex(newStart);
      return newZoom;
    });
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartIndex.current = startIndex;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    const chartWidth = chartRef.current.offsetWidth;
    const visibleCount = Math.floor(data.length / zoomFactor);
    const deltaIndex = Math.floor((dx / chartWidth) * visibleCount);
    let newStart = dragStartIndex.current - deltaIndex;
    newStart = Math.max(0, Math.min(data.length - visibleCount, newStart));
    setStartIndex(newStart);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const getZoomedData = () => {
    const visibleCount = Math.floor(data.length / zoomFactor);
    return data.slice(startIndex, startIndex + visibleCount);
  };

  const handleLegendClick = (e) => {
    const { dataKey } = e;
    setHiddenLines((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const renderCustomLegend = ({ payload }) => (
    <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginBottom: "10px" }}>
      {payload.map((entry) => (
        <span
          key={entry.dataKey}
          onClick={() => handleLegendClick(entry)}
          style={{
            cursor: "pointer",
            color: hiddenLines[entry.dataKey] ? "#ccc" : entry.color,
            textDecoration: hiddenLines[entry.dataKey] ? "line-through" : "none",
            fontWeight: "bold",
          }}
        >
          
          ● {(translate(entry.value) || '')?.charAt(0).toUpperCase() + (translate(entry.value) || '')?.slice(1)}
        </span>
      ))}
    </div>
  );

  const dataKeys = Object.keys(seriesData || {});
  const leftKeys = dataKeys.filter((_, i) => i % 2 === 0);
  const rightKeys = dataKeys.filter((_, i) => i % 2 !== 0);

  return (
    <div
      ref={chartRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{ width: "100%", height: 450, background: "white", userSelect: "none" }}
    >
      <ResponsiveContainer>
        <LineChart data={getZoomedData()}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" />
          <YAxis yAxisId="left" orientation="left" />
          <YAxis yAxisId="right" orientation="right" />
        <Tooltip
  formatter={(value, name) => {
    const unitMap = {
      power: 'V',
      voltage: 'V',
      distance: 'km',
      speed: 'km/h',
      temperature: '°C',
      fuel: 'L',
    };

    const unitEntry = Object.entries(unitMap).find(([k]) => name.toLowerCase().includes(k));
    const unit = unitEntry ? unitEntry[1] : '';

    return [`${value} ${unit}`, name];
  }}
/>

          <Legend content={renderCustomLegend} verticalAlign="bottom" />

          {leftKeys.map((key, idx) => (
            <Line
              key={key}
              yAxisId="left"
              type="monotone"
              dataKey={key}
              stroke={colorPalette[idx % colorPalette.length]}
              strokeWidth={2}
              dot={false}
              hide={hiddenLines[key]}
              isAnimationActive={false}
            />
          ))}

          {rightKeys.map((key, idx) => (
            <Line
              key={key}
              yAxisId="right"
              type="monotone"
              dataKey={key}
              stroke={colorPalette[(idx + leftKeys.length) % colorPalette.length]}
              strokeWidth={2}
              dot={false}
              hide={hiddenLines[key]}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DynamicLineGraph;
